import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import IASideBar from './IASideBar';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import DynamicSnackbar from '../../common/Snackbar';
import CustomSpeedDial from '../../common/SpeedDial';
import * as mui from '@mui/material';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Chip,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    CircularProgress,
    Alert,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText as MuiListItemText,
    Avatar,
    Breadcrumbs,
    Link,
    Tooltip,
} from '@mui/material';
import {
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    Send as SendIcon,
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AccessTime as AccessTimeIcon,
    Description as DescriptionIcon,
    Attachment as AttachmentIcon,
    Pending as PendingIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    ErrorOutline as ErrorOutlineIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    InsertDriveFile as InsertDriveFileIcon,
    PictureAsPdf as PictureAsPdfIcon,
    TableChart as TableChartIcon,
    Image as ImageIcon,
    Article as ArticleIcon,
    CloudCircle as CloudCircleIcon,
    MoreVert as MoreVertIcon,
    FileDownload as FileDownloadIcon,
    Search as SearchIcon,
    NotificationsActive as NotificationsActiveIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import auditService from '../../../services/AuditService';
import userService from '../../../services/UserService';
import roleService from '../../../services/RoleService';

// Hey there! 👋 This is our Audit Request Details component.
// It handles displaying and managing all the details of an audit request.

// Let's style our components to make them look nice! 🎨
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: 'transparent',
    border: '1px solid rgba(0, 0, 0, 0.08)',
}));

// These styled components help us maintain consistent typography throughout the app
const SectionTitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '1.1rem',
    letterSpacing: '0.02em',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

// Labels for our detail fields - keeping it clean and readable
const DetailLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
    fontSize: '0.813rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
}));

// The actual values in our detail fields - making sure they stand out appropriately
const DetailValue = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
    fontSize: '0.938rem',
    color: theme.palette.text.primary,
    fontWeight: 400,
}));

// This is where the magic happens! 🚀
// Our main component that brings everything together
const AuditRequestDetails = () => {
    // Get our URL parameters - we'll need these to fetch the right data
    const { project_id, id } = useParams();
    const navigate = useNavigate();

    // Set up our state management - keeping track of everything we need
    const [request, setRequest] = useState(null);          // The main audit request data
    const [loading, setLoading] = useState(true);         // Loading state for API calls
    const [error, setError] = useState(null);             // Any errors we need to show
    const [comment, setComment] = useState('');           // New comments being written
    const [comments, setComments] = useState([]);         // Existing comments
    const [files, setFiles] = useState([]);               // Attached files
    const [uploadProgress, setUploadProgress] = useState(0);  // Track file upload progress
    const [isUploading, setIsUploading] = useState(false);   // Are we currently uploading?
    
    // UI state management
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [currentUser, setCurrentUser] = useState(null);
    
    // Menu handling state
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [assigneeAnchorEl, setAssigneeAnchorEl] = useState(null);
    const [users, setUsers] = useState([]);
    const assigneeMenuOpen = Boolean(assigneeAnchorEl);
    
    // Project and file handling state
    const [selectedProject, setSelectedProject] = useState([]);
    const fileInputRef = React.useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, fileId: null });
    const [editingComment, setEditingComment] = useState(null);
    const [editedCommentText, setEditedCommentText] = useState('');
    const [commentMenuAnchorEl, setCommentMenuAnchorEl] = useState(null);
    const [selectedComment, setSelectedComment] = useState(null);
    const [deleteCommentConfirm, setDeleteCommentConfirm] = useState({ open: false, commentId: null });
    const [clientUsers, setClientUsers] = useState([]);
    const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');

    // Add these state variables after the other state declarations
    const [editingField, setEditingField] = useState(null);
    const [editedDescription, setEditedDescription] = useState('');
    const [editedComments, setEditedComments] = useState('');
    const [editedPriority, setEditedPriority] = useState('');
    const [editedDueDate, setEditedDueDate] = useState('');
    const [priorityAnchorEl, setPriorityAnchorEl] = useState(null);
    const priorityMenuOpen = Boolean(priorityAnchorEl);
    const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);

    // 🎨 Helper function to show the right icon for different file types
    const getFileIcon = (fileName) => {
        if (!fileName) return <InsertDriveFileIcon sx={{ color: '#757575' }} />;
        const extension = fileName.split('.').pop().toLowerCase();
        
        switch (extension) {
            case 'pdf':
                return <PictureAsPdfIcon sx={{ color: '#ff5252' }} />;
            case 'doc':
            case 'docx':
                return <DescriptionIcon sx={{ color: '#2196f3' }} />;
            case 'xls':
            case 'xlsx':
            case 'csv':
                return <TableChartIcon sx={{ color: '#4caf50' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return <ImageIcon sx={{ color: '#ff9800' }} />;
            case 'txt':
                return <ArticleIcon sx={{ color: '#757575' }} />;
            default:
                return <InsertDriveFileIcon sx={{ color: '#757575' }} />;
        }
    };

    // ⏰ Calculate how many days until (or since) the due date
    const calculateDueStatus = (dueDate) => {
        if (!dueDate) return null;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(dueDate);
        due.setHours(0, 0, 0, 0);
        
        const diffTime = due - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return {
            daysRemaining: diffDays,
            isOverdue: diffDays < 0,
            isToday: diffDays === 0
        };
    };

    // 🔄 Load all our data when the component mounts
    useEffect(() => {
        fetchRequestDetails();
        fetchCurrentUser();
        fetchUsers();
        fetchProjectData();
        fetchClientUsers();
    }, [project_id, id]);

    const fetchProjectData = async () => {
        try {
            setLoading(true);
            const projectRecord = await auditService.fetchProjectsById(project_id);
            if (projectRecord) {
                setSelectedProject(projectRecord)
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentUser = async () => {
        const user = await userService.fetchCurrentUser();
        if (user) {
            setCurrentUser(user);
        }
    };

    const fetchRequestDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await auditService.fetchRequestById(id);
            if (response && response.data) {
                setRequest(response.data);
                // Fetch comments separately
                const commentsResponse = await auditService.getComments(id);
                setComments(commentsResponse || []);
                setFiles(response.data.evidence_files || []);
            } else {
                setError('No audit request found or invalid response format');
            }
        } catch (err) {
            console.error('Error fetching request details:', err);
            setError('Failed to fetch request details. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await userService.fetchUsers();
            if (response && response.data) {
                setUsers(response.data);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const fetchClientUsers = async () => {
        try {
            const usersData = await roleService.fetchUsers();
            // Create a Set to track unique user values
            const uniqueUsers = new Set();
            
            // Filter users and ensure uniqueness
            const clientRoleUsers = usersData
                .filter(user => 
                    user.role.role_name === 'Client Participant' || 
                    user.role.role_name === 'Client Coordinator'
                )
                .reduce((acc, user) => {
                    const fullName = `${user.user.first_name} ${user.user.last_name}`;
                    // Only add the user if we haven't seen this name before
                    if (!uniqueUsers.has(fullName)) {
                        uniqueUsers.add(fullName);
                        acc.push({
                            value: user.user.email,
                            label: `${fullName} (${user.role.role_name})`,
                            fullName: fullName,
                        });
                    }
                    return acc;
                }, []);

            // Sort users alphabetically by name
            clientRoleUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));
            
            setClientUsers(clientRoleUsers);
        } catch (error) {
            console.error('Error fetching client users:', error);
        }
    };

    const handleStatusClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleStatusClose = () => {
        setAnchorEl(null);
    };

    const handleStatusSelect = async (status) => {
        try {
            setLoading(true);
            const user = `${currentUser.first_name} ${currentUser.last_name}`;
            
            // Include all required fields from the existing request
            const updateData = {
                STATUS: status,
                UPDATED_BY: user,
                SYSTEM_NAME: request.SYSTEM_NAME,
                REQUEST_TYPE: request.REQUEST_TYPE,
                DESCRIPTION: request.DESCRIPTION,
                DUE_DATE: request.DUE_DATE,
                ASSIGNEE: request.ASSIGNEE,
                CREATED_BY: request.CREATED_BY
            };
            
            const response = await auditService.updateRequest(id, updateData);
            
            // Check if we received a response with an id (indicating success)
            if (response && response.id) {
                setRequest(response); // Update with the full response as it contains the latest data
                handleStatusClose();
                showSnackbar('Status updated successfully', 'success');
            } else {
                console.error('Unexpected response format:', response);
                throw new Error('Unexpected response from server');
            }
        } catch (err) {
            console.error('Error details:', {
                message: err.message,
                response: err.response,
                data: err.response?.data,
                status: err.response?.status
            });
            
            const errorMessage = err.response?.data?.detail || 
                               err.response?.data?.message || 
                               err.message || 
                               'Failed to update status';
            showSnackbar(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAssigneeClick = (event) => {
        setAssigneeAnchorEl(event.currentTarget);
    };

    const handleAssigneeClose = () => {
        setAssigneeAnchorEl(null);
    };

    const handleAssigneeSelect = async (newAssignee) => {
        try {
            setLoading(true);
            const user = `${currentUser.first_name} ${currentUser.last_name}`;
            
            // Include all required fields from the existing request
            const updateData = {
                ASSIGNEE: newAssignee,
                UPDATED_BY: user,
                SYSTEM_NAME: request.SYSTEM_NAME,
                REQUEST_TYPE: request.REQUEST_TYPE,
                DESCRIPTION: request.DESCRIPTION,
                DUE_DATE: request.DUE_DATE,
                CREATED_BY: request.CREATED_BY,
                STATUS: request.STATUS
            };
            
            const response = await auditService.updateRequest(id, updateData);
            
            if (response) {
                setRequest({ ...request, ASSIGNEE: newAssignee });
                handleAssigneeClose();
                showSnackbar('Assignee updated successfully', 'success');
            }
        } catch (err) {
            console.error('Error updating assignee:', err);
            showSnackbar(err.response?.data?.detail || 'Failed to update assignee', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCommentSubmit = async () => {
        if (!comment.trim()) return;

        try {
            setLoading(true);
            const user = `${currentUser.first_name} ${currentUser.last_name}`;
            const commentData = {
                comment_text: comment,
                created_by: user
            };

            await auditService.addComment(id, commentData);
            
            // Refresh comments
            const commentsResponse = await auditService.getComments(id);
            setComments(commentsResponse || []);
            setComment('');
            showSnackbar('Comment added successfully', 'success');
        } catch (err) {
            console.error('Error adding comment:', err);
            showSnackbar(err.response?.data?.detail || 'Failed to add comment', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.style.borderColor = '#2196f3';
        event.currentTarget.style.backgroundColor = '#f5f5f5';
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.style.borderColor = '#bdbdbd';
        event.currentTarget.style.backgroundColor = '#fafafa';
        setDragActive(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            handleFiles(event.dataTransfer.files);
        }
    };

    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            handleFiles(event.target.files);
            event.target.value = '';
        }
    };

    const handleFiles = async (uploadedFiles) => {
        if (!uploadedFiles || uploadedFiles.length === 0) {
            return;
        }

        try {
            setIsUploading(true);
            setUploadProgress(0);
            const formData = new FormData();
            
            // Check file size and add files to FormData
            const maxSize = 20 * 1024 * 1024; // 20MB in bytes
            let hasLargeFile = false;
            let largeFileName = '';

            // Add each file to FormData with key 'files'
            Array.from(uploadedFiles).forEach(file => {
                if (file.size > maxSize) {
                    hasLargeFile = true;
                    largeFileName = file.name;
                    return;
                }
                formData.append('files', file);
                formData.append('original_filename', file.name);
                formData.append('file_size', file.size);
                formData.append('file_type', file.type);
            });

            if (hasLargeFile) {
                showSnackbar(`File "${largeFileName}" exceeds 20MB limit`, 'error');
                setIsUploading(false);
                setUploadProgress(0);
                return;
            }

            // Add the request ID
            formData.append('audit_request', id);
            formData.append('uploaded_by', `${currentUser.first_name} ${currentUser.last_name}`);
            formData.append('upload_date', new Date().toISOString());

            // Simulate upload progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 300);

            // Upload the files
            const uploadResponse = await auditService.uploadAuditRequestEvidence(formData);
            
            if (uploadResponse && uploadResponse.data) {
                clearInterval(progressInterval);
                setUploadProgress(100);
                
                // Update the local state with the new files
                const updatedRequest = await auditService.fetchRequestById(id);
                if (updatedRequest && updatedRequest.data) {
                    setFiles(updatedRequest.data.evidence_files || []);
                    showSnackbar('Files uploaded successfully', 'success');
                }
                
                // Reset progress after a short delay
                setTimeout(() => {
                    setUploadProgress(0);
                    setIsUploading(false);
                }, 500);
            }
        } catch (err) {
            console.error('Error uploading files:', err);
            showSnackbar('Failed to upload files', 'error');
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleFileDelete = async (fileId) => {
        setDeleteConfirm({ open: true, fileId });
    };

    const confirmDelete = async () => {
        try {
            setLoading(true);
            await auditService.deleteRequestEvidence(deleteConfirm.fileId);
            
            // Refresh the request details to get updated files
            const refreshResponse = await auditService.fetchRequestById(id);
            if (refreshResponse && refreshResponse.data) {
                setFiles(refreshResponse.data.evidence_files || []);
                showSnackbar('File deleted successfully', 'success');
            }
        } catch (err) {
            console.error('Error deleting file:', err);
            showSnackbar('Failed to delete file', 'error');
        } finally {
            setLoading(false);
            setDeleteConfirm({ open: false, fileId: null });
        }
    };

    const handleFileDownload = async (file) => {
        try {
            window.open(file.file_name, '_blank');
        } catch (error) {
            console.error('Error downloading file:', error);
            showSnackbar('Failed to download file', 'error');
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCommentMenuClick = (event, comment) => {
        setCommentMenuAnchorEl(event.currentTarget);
        setSelectedComment(comment);
    };

    const handleCommentMenuClose = () => {
        setCommentMenuAnchorEl(null);
        setSelectedComment(null);
    };

    const handleEditComment = (comment) => {
        setEditingComment(comment);
        setEditedCommentText(comment.comment_text);
        handleCommentMenuClose();
    };

    const handleCancelEdit = () => {
        setEditingComment(null);
        setEditedCommentText('');
    };

    const handleSaveEdit = async (commentId) => {
        try {
            setLoading(true);
            const commentData = {
                comment_text: editedCommentText,
                updated_by: `${currentUser.first_name} ${currentUser.last_name}`
            };

            await auditService.updateComment(id, commentId, commentData);
            
            // Refresh comments
            const commentsResponse = await auditService.getComments(id);
            setComments(commentsResponse || []);
            
            setEditingComment(null);
            setEditedCommentText('');
            showSnackbar('Comment updated successfully', 'success');
        } catch (err) {
            console.error('Error updating comment:', err);
            showSnackbar(err.response?.data?.detail || 'Failed to update comment', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteComment = async () => {
        try {
            setLoading(true);
            await auditService.deleteComment(id, deleteCommentConfirm.commentId);
            
            // Refresh comments
            const commentsResponse = await auditService.getComments(id);
            setComments(commentsResponse || []);
            
            setDeleteCommentConfirm({ open: false, commentId: null });
            showSnackbar('Comment deleted successfully', 'success');
        } catch (err) {
            console.error('Error deleting comment:', err);
            showSnackbar(err.response?.data?.detail || 'Failed to delete comment', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Add this function before the Menu component
    const filteredClientUsers = clientUsers.filter(user =>
        user.label.toLowerCase().includes(assigneeSearchQuery.toLowerCase()) &&
        user.value !== request?.ASSIGNEE
    );

    // Add this function after the other handle functions
    const handleFieldEdit = async (field, value) => {
        try {
            setLoading(true);
            const user = `${currentUser.first_name} ${currentUser.last_name}`;
            
            const updateData = {
                ...request,
                [field]: value,
                UPDATED_BY: user
            };
            
            const response = await auditService.updateRequest(id, updateData);
            
            if (response) {
                setRequest(response);
                setEditingField(null);
                showSnackbar('Updated successfully', 'success');
            }
        } catch (err) {
            console.error('Error updating field:', err);
            showSnackbar(err.response?.data?.detail || 'Failed to update', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePriorityClick = (event) => {
        setPriorityAnchorEl(event.currentTarget);
    };

    const handlePriorityClose = () => {
        setPriorityAnchorEl(null);
    };

    const handlePrioritySelect = async (priority) => {
        await handleFieldEdit('PRIORITY', priority);
        handlePriorityClose();
    };

    const handleFollowUp = async () => {
        try {
            setIsFollowUpLoading(true);
            await auditService.sendFollowUp(id);
            showSnackbar('Follow-up email sent successfully', 'success');
        } catch (err) {
            console.error('Error sending follow-up:', err);
            showSnackbar(err.response?.data?.detail || 'Failed to send follow-up email', 'error');
        } finally {
            setIsFollowUpLoading(false);
        }
    };

    const handleSpeedDialClick = () => {
        navigate(`/audit/project/${id}/requests/new`);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }
    const customMainContent = (
        <ResponsiveContainer>
            <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/dashboard">
                        <i className="material-icons">home</i>
                    </Link>
                    <Link underline="hover" color="inherit" href="/dashboard">
                        My Dashboard
                    </Link>
                    <Link underline="hover" color="inherit" href={`/Audit/Projects/${selectedProject.id}`}>
                        {selectedProject.AUDIT_NAME}
                    </Link>
                    <Link underline="hover" color="inherit" href= {`/audit/auditrequests/${selectedProject.id}`}>
                        Audit Requests
                    </Link>
                    <Typography color="text.primary">{request?.REQUEST_ID}</Typography>

                   
                   
                </Breadcrumbs>
            <Paper 
                sx={{ 
                    p: 3,
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    minHeight: 'calc(100vh - 120px)'
                }}
            >
                <Grid container spacing={3}>
                    {/* Request Details Section */}
                    <Grid item xs={12}>
                        <StyledPaper sx={{ boxShadow: 'none' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <SectionTitle variant="h5">
                                    <AccessTimeIcon sx={{ fontSize: '1.25rem', color: 'primary.main' }} />
                                    Request Details
                                </SectionTitle>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            label={
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    {request?.STATUS}
                                                    <EditIcon sx={{ 
                                                        fontSize: '0.875rem', 
                                                        opacity: 0.7,
                                                        transition: 'opacity 0.2s',
                                                        ml: 0.5,
                                                        color: 'white'
                                                    }} />
                                                </Box>
                                            }
                                            color={
                                                request?.STATUS === 'Completed' ? 'success' :
                                                request?.STATUS === 'Pending' ? 'warning' :
                                                request?.STATUS === 'In Review' ? 'info' :
                                                request?.STATUS === 'Evidence Submitted' ? 'secondary' :
                                                'error'
                                            }
                                            onClick={handleStatusClick}
                                            sx={{ 
                                                fontWeight: 500,
                                                px: 1,
                                                py: 2.5,
                                                borderRadius: 2,
                                                fontSize: '0.875rem',
                                                height: '32px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    opacity: 0.9,
                                                    '& .MuiSvgIcon-root': {
                                                        opacity: 1
                                                    }
                                                },
                                                '& .MuiChip-label': {
                                                    px: 1
                                                }
                                            }}
                                        />
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleStatusClose}
                                            PaperProps={{
                                                elevation: 3,
                                                sx: {
                                                    mt: 1,
                                                    borderRadius: 2,
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                    minWidth: 300,
                                                    maxWidth: 360,
                                                    overflow: 'hidden'
                                                }
                                            }}
                                        >
                                            <Box sx={{ 
                                                p: 2, 
                                                pb: 1.5,
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                                            }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'text.primary'
                                                    }}
                                                >
                                                    Update Status
                                                </Typography>
                                            </Box>
                                            <Box sx={{ py: 1 }}>
                                                <MenuItem 
                                                    onClick={() => handleStatusSelect('Pending')}
                                                    sx={{ 
                                                        py: 1.5,
                                                        px: 2,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 152, 0, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <Avatar sx={{ 
                                                            width: 28, 
                                                            height: 28, 
                                                            bgcolor: 'warning.main',
                                                            color: 'white'
                                                        }}>
                                                            <PendingIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <Box>
                                                        <Typography 
                                                            variant="body2"
                                                            sx={{ 
                                                                fontWeight: 500,
                                                                color: 'text.primary',
                                                                mb: 0.25
                                                            }}
                                                        >
                                                            Pending
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption"
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                display: 'block',
                                                                lineHeight: 1
                                                            }}
                                                        >
                                                            Request is awaiting review
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem 
                                                    onClick={() => handleStatusSelect('In Review')}
                                                    sx={{ 
                                                        py: 1.5,
                                                        px: 2,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(33, 150, 243, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <Avatar sx={{ 
                                                            width: 28, 
                                                            height: 28, 
                                                            bgcolor: 'info.main',
                                                            color: 'white'
                                                        }}>
                                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="In Review"
                                                        secondary="Request is being reviewed"
                                                        primaryTypographyProps={{
                                                            variant: 'body1',
                                                            fontWeight: 600,
                                                            color: 'text.primary'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: 'caption',
                                                            color: 'text.secondary'
                                                        }}
                                                    />
                                                </MenuItem>
                                                <MenuItem 
                                                    onClick={() => handleStatusSelect('Evidence Submitted')}
                                                    sx={{ 
                                                        py: 1.5,
                                                        px: 2,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(156, 39, 176, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <Avatar sx={{ 
                                                            width: 28, 
                                                            height: 28, 
                                                            bgcolor: '#9c27b0',
                                                            color: 'white'
                                                        }}>
                                                            <AttachFileIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Evidence Submitted"
                                                        secondary="Evidence has been uploaded"
                                                        primaryTypographyProps={{
                                                            variant: 'body1',
                                                            fontWeight: 600,
                                                            color: 'text.primary'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: 'caption',
                                                            color: 'text.secondary'
                                                        }}
                                                    />
                                                </MenuItem>
                                                <MenuItem 
                                                    onClick={() => handleStatusSelect('Completed')}
                                                    sx={{ 
                                                        py: 1.5,
                                                        px: 2,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(76, 175, 80, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <Avatar sx={{ 
                                                            width: 28, 
                                                            height: 28, 
                                                            bgcolor: 'success.main',
                                                            color: 'white'
                                                        }}>
                                                            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <Box>
                                                        <Typography 
                                                            variant="body2"
                                                            sx={{ 
                                                                fontWeight: 500,
                                                                color: 'text.primary',
                                                                mb: 0.25
                                                            }}
                                                        >
                                                            Completed
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption"
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                display: 'block',
                                                                lineHeight: 1
                                                            }}
                                                        >
                                                            Review process completed
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                                <MenuItem 
                                                    onClick={() => handleStatusSelect('Resubmission Needed')}
                                                    sx={{ 
                                                        py: 1.5,
                                                        px: 2,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <Avatar sx={{ 
                                                            width: 28, 
                                                            height: 28, 
                                                            bgcolor: 'error.main',
                                                            color: 'white'
                                                        }}>
                                                            <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </ListItemIcon>
                                                    <Box>
                                                        <Typography 
                                                            variant="body2"
                                                            sx={{ 
                                                                fontWeight: 500,
                                                                color: 'text.primary',
                                                                mb: 0.25
                                                            }}
                                                        >
                                                            Resubmission Needed
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption"
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                display: 'block',
                                                                lineHeight: 1
                                                            }}
                                                        >
                                                            Additional information required
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            </Box>
                                        </Menu>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip
                                            label={
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    {clientUsers.find(user => user.value === request?.ASSIGNEE)?.fullName || request?.ASSIGNEE}
                                                    <EditIcon sx={{ 
                                                        fontSize: '0.875rem', 
                                                        opacity: 0.7,
                                                        transition: 'opacity 0.2s',
                                                        ml: 0.5
                                                    }} />
                                                </Box>
                                            }
                                            onClick={handleAssigneeClick}
                                            avatar={
                                                <Avatar sx={{ 
                                                    width: 24, 
                                                    height: 24, 
                                                    bgcolor: 'primary.main',
                                                    color: 'white',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}>
                                                    <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                                                </Avatar>
                                            }
                                            sx={{ 
                                                fontWeight: 500,
                                                px: 1,
                                                py: 2.5,
                                                borderRadius: 2,
                                                fontSize: '0.875rem',
                                                height: '32px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                    '& .MuiSvgIcon-root': {
                                                        opacity: 1
                                                    }
                                                },
                                                '& .MuiChip-label': {
                                                    px: 1.5,
                                                    ml: 0.5
                                                },
                                                '& .MuiChip-avatar': {
                                                    ml: 0.5
                                                }
                                            }}
                                        />
                                        <Tooltip title="Send Follow-Up Email" arrow placement="top">
                                            <IconButton
                                                size="small"
                                                onClick={handleFollowUp}
                                                disabled={isFollowUpLoading}
                                                sx={{
                                                    height: '32px',
                                                    width: '32px',
                                                    color: 'text.secondary',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        color: 'primary.main'
                                                    }
                                                }}
                                            >
                                                {isFollowUpLoading ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <NotificationsActiveIcon sx={{ fontSize: 16 }} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            anchorEl={assigneeAnchorEl}
                                            open={assigneeMenuOpen}
                                            onClose={handleAssigneeClose}
                                            PaperProps={{
                                                elevation: 3,
                                                sx: {
                                                    mt: 1,
                                                    borderRadius: 2,
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                    minWidth: 300,
                                                    maxWidth: 360,
                                                    overflow: 'hidden'
                                                }
                                            }}
                                        >
                                            <Box sx={{ 
                                                p: 2, 
                                                pb: 1.5,
                                                borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                                            }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        mb: 1.5,
                                                        fontWeight: 600,
                                                        color: 'text.primary'
                                                    }}
                                                >
                                                    Assign Request
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Search assignee..."
                                                    value={assigneeSearchQuery}
                                                    onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <mui.InputAdornment position="start">
                                                                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                            </mui.InputAdornment>
                                                        ),
                                                        sx: {
                                                            fontSize: '0.875rem',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(0, 0, 0, 0.08)'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ 
                                                maxHeight: 320, 
                                                overflow: 'auto',
                                                py: 1
                                            }}>
                                                {filteredClientUsers.length === 0 ? (
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        textAlign: 'center',
                                                        color: 'text.secondary'
                                                    }}>
                                                        <Typography variant="body2">
                                                            No assignees found
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    filteredClientUsers.map((user) => (
                                                        <MenuItem 
                                                            key={user.value}
                                                            onClick={() => handleAssigneeSelect(user.value)}
                                                            sx={{ 
                                                                py: 1,
                                                                px: 2,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                                }
                                                            }}
                                                        >
                                                            <ListItemIcon>
                                                                <Avatar sx={{ 
                                                                    width: 28, 
                                                                    height: 28, 
                                                                    bgcolor: 'primary.main',
                                                                    color: 'white',
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 600
                                                                }}>
                                                                    {user.fullName.charAt(0).toUpperCase()}
                                                                </Avatar>
                                                            </ListItemIcon>
                                                            <Box>
                                                                <Typography 
                                                                    variant="body2"
                                                                    sx={{ 
                                                                        fontWeight: 500,
                                                                        color: 'text.primary',
                                                                        mb: 0.25
                                                                    }}
                                                                >
                                                                    {user.fullName}
                                                                </Typography>
                                                                <Typography 
                                                                    variant="caption"
                                                                    sx={{ 
                                                                        color: 'text.secondary',
                                                                        display: 'block',
                                                                        lineHeight: 1
                                                                    }}
                                                                >
                                                                    {user.label.split('(')[1].replace(')', '')}
                                                                </Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Box>
                                        </Menu>
                                    </Box>
                                </Box>
                            </Box>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <DetailLabel>Request ID</DetailLabel>
                                    <DetailValue>{request?.REQUEST_ID}</DetailValue>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailLabel>System Name</DetailLabel>
                                    <DetailValue>{request?.APP_NAME}</DetailValue>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailLabel>Request Type</DetailLabel>
                                    <DetailValue>{request?.REQUEST_TYPE}</DetailValue>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailLabel>Phase</DetailLabel>
                                    <DetailValue>{request?.PHASE}</DetailValue>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailLabel>Priority</DetailLabel>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label={
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    {request?.PRIORITY}
                                                    <EditIcon sx={{ 
                                                        fontSize: '0.875rem', 
                                                        opacity: 0.7,
                                                        transition: 'opacity 0.2s',
                                                        ml: 0.5,
                                                        color: 'inherit'
                                                    }} />
                                                </Box>
                                            }
                                            onClick={handlePriorityClick}
                                            sx={{ 
                                                fontWeight: 500,
                                                height: '32px',
                                                cursor: 'pointer',
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                border: '1px solid rgba(0, 0, 0, 0.08)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                                                }
                                            }}
                                        />
                                        <Menu
                                            anchorEl={priorityAnchorEl}
                                            open={priorityMenuOpen}
                                            onClose={handlePriorityClose}
                                            PaperProps={{
                                                elevation: 1,
                                                sx: {
                                                    mt: 1,
                                                    minWidth: 120
                                                }
                                            }}
                                        >
                                            <MenuItem onClick={() => handlePrioritySelect('High')}>
                                                High
                                            </MenuItem>
                                            <MenuItem onClick={() => handlePrioritySelect('Medium')}>
                                                Medium
                                            </MenuItem>
                                            <MenuItem onClick={() => handlePrioritySelect('Low')}>
                                                Low
                                            </MenuItem>
                                        </Menu>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DetailLabel>Due Date</DetailLabel>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {editingField === 'DUE_DATE' ? (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <TextField
                                                    type="date"
                                                    size="small"
                                                    value={editedDueDate || request?.DUE_DATE?.split('T')[0]}
                                                    onChange={(e) => setEditedDueDate(e.target.value)}
                                                    sx={{ minWidth: 200 }}
                                                />
                                                <Button 
                                                    size="small" 
                                                    variant="contained"
                                                    onClick={() => handleFieldEdit('DUE_DATE', editedDueDate)}
                                                >
                                                    Save
                                                </Button>
                                                <Button 
                                                    size="small"
                                                    onClick={() => setEditingField(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DetailValue sx={{ mb: 0 }}>
                                                    {new Date(request?.DUE_DATE).toLocaleDateString()}
                                                </DetailValue>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => setEditingField('DUE_DATE')}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                        {request?.DUE_DATE && request?.STATUS === 'Pending' && (() => {
                                            const dueStatus = calculateDueStatus(request.DUE_DATE);
                                            if (!dueStatus) return null;

                                            if (dueStatus.isOverdue) {
                                                return (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ 
                                                            color: 'error.main',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        (Overdue by {Math.abs(dueStatus.daysRemaining)} {Math.abs(dueStatus.daysRemaining) === 1 ? 'day' : 'days'})
                                                    </Typography>
                                                );
                                            } else if (dueStatus.isToday) {
                                                return (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        (Due today)
                                                    </Typography>
                                                );
                                            } else {
                                                return (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                         ({dueStatus.daysRemaining} {dueStatus.daysRemaining === 1 ? 'day' : 'days'} till due date)
                                                    </Typography>
                                                );
                                            }
                                        })()}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <DetailLabel>Description</DetailLabel>
                                    {editingField === 'DESCRIPTION' ? (
                                        <Box sx={{ mt: 1 }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={editedDescription || request?.DESCRIPTION}
                                                onChange={(e) => setEditedDescription(e.target.value)}
                                                sx={{ mb: 1 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button 
                                                    variant="contained"
                                                    onClick={() => handleFieldEdit('DESCRIPTION', editedDescription)}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    onClick={() => setEditingField(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ position: 'relative' }}>
                                            <DetailValue sx={{ 
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: 1.5,
                                                color: 'rgba(0, 0, 0, 0.87)',
                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                p: 2,
                                                borderRadius: 1,
                                                border: '1px solid rgba(0, 0, 0, 0.06)'
                                            }}>
                                                {request?.DESCRIPTION}
                                            </DetailValue>
                                            <IconButton 
                                                size="small"
                                                onClick={() => setEditingField('DESCRIPTION')}
                                                sx={{ 
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Grid>
                                {request?.COMMENTS && (
                                    <Grid item xs={12}>
                                        <DetailLabel>Requestor Comments</DetailLabel>
                                        {editingField === 'COMMENTS' ? (
                                            <Box sx={{ mt: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    value={editedComments || request?.COMMENTS}
                                                    onChange={(e) => setEditedComments(e.target.value)}
                                                    sx={{ mb: 1 }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    <Button 
                                                        variant="contained"
                                                        onClick={() => handleFieldEdit('COMMENTS', editedComments)}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        onClick={() => setEditingField(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{ position: 'relative' }}>
                                                <DetailValue sx={{ 
                                                    whiteSpace: 'pre-wrap',
                                                    lineHeight: 1.5,
                                                    color: 'rgba(0, 0, 0, 0.87)',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                    p: 2,
                                                    borderRadius: 1,
                                                    border: '1px solid rgba(0, 0, 0, 0.06)'
                                                }}>
                                                    {request?.COMMENTS}
                                                </DetailValue>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => setEditingField('COMMENTS')}
                                                    sx={{ 
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </StyledPaper>
                    </Grid>

                    {/* Evidence Section */}
                    <Grid item xs={12}>
                        <StyledPaper sx={{ boxShadow: 'none' }}>
                            <SectionTitle variant="h6">
                                <AttachmentIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
                                Evidence
                            </SectionTitle>
                            <Box>
                                {/* Requestor's Files Section */}
                                {files.filter(file => file.uploaded_by === request?.CREATED_BY).length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ 
                                            mb: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: 'text.secondary'
                                        }}>
                                            <PersonIcon sx={{ fontSize: '1rem' }} />
                                            Files from Requestor
                                        </Typography>
                                        <Paper variant="outlined" sx={{ maxHeight: '220px', overflow: 'auto' }}>
                                            {files
                                                .filter(file => file.uploaded_by === request?.CREATED_BY)
                                                .map((file, index, filteredFiles) => (
                                                    <Box
                                                        key={file.id || index}
                                                        sx={{
                                                            p: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            borderBottom: index < filteredFiles.length - 1 ? '1px solid #eee' : 'none',
                                                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(25, 118, 210, 0.08)'
                                                            }
                                                        }}
                                                    >
                                                        <Box 
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                flexGrow: 1, 
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    color: 'primary.main'
                                                                }
                                                            }}
                                                            onClick={() => handleFileDownload(file)}
                                                        >
                                                            <Box sx={{ mr: 1 }}>
                                                                {getFileIcon(file.original_filename)}
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" noWrap>
                                                                    {file.original_filename}
                                                                </Typography>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="textSecondary"
                                                                    sx={{ display: 'block' }}
                                                                >
                                                                    {(file.file_size / (1024 * 1024)).toFixed(2)} MB • {new Date(file.upload_date).toLocaleDateString()}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {(() => {
                                                            console.log('File uploader:', file.uploaded_by);
                                                            console.log('Current user:', `${currentUser?.first_name} ${currentUser?.last_name}`);
                                                            console.log('Current user role:', currentUser?.role?.role_name);
                                                            // Show delete icon if the user is the uploader or has appropriate role
                                                            return (
                                                                <IconButton 
                                                                    size="small"
                                                                    onClick={() => handleFileDelete(file.id)}
                                                                    sx={{ 
                                                                        color: 'error.main',
                                                                        ml: 1,
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            );
                                                        })()}
                                                    </Box>
                                                ))}
                                        </Paper>
                                    </Box>
                                )}

                                {/* Other Files Section */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ 
                                        mb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        color: 'text.secondary'
                                    }}>
                                        <AttachmentIcon sx={{ fontSize: '1rem' }} />
                                        Additional Evidence Files
                                    </Typography>

                                    {/* Display Additional Evidence Files */}
                                    {files.filter(file => file.uploaded_by !== request?.CREATED_BY).length > 0 && (
                                        <Paper variant="outlined" sx={{ mb: 2, maxHeight: '220px', overflow: 'auto' }}>
                                            {files
                                                .filter(file => file.uploaded_by !== request?.CREATED_BY)
                                                .map((file, index, filteredFiles) => (
                                                    <Box
                                                        key={file.id || index}
                                                        sx={{
                                                            p: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            borderBottom: index < filteredFiles.length - 1 ? '1px solid #eee' : 'none',
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5'
                                                            }
                                                        }}
                                                    >
                                                        <Box 
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                flexGrow: 1, 
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    color: 'primary.main'
                                                                }
                                                            }}
                                                            onClick={() => handleFileDownload(file)}
                                                        >
                                                            <Box sx={{ mr: 1 }}>
                                                                {getFileIcon(file.original_filename)}
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" noWrap>
                                                                    {file.original_filename}
                                                                </Typography>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="textSecondary"
                                                                    sx={{ display: 'block' }}
                                                                >
                                                                    {(file.file_size / (1024 * 1024)).toFixed(2)} MB • {new Date(file.upload_date).toLocaleDateString()}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {(() => {
                                                            console.log('File uploader:', file.uploaded_by);
                                                            console.log('Current user:', `${currentUser?.first_name} ${currentUser?.last_name}`);
                                                            console.log('Current user role:', currentUser?.role?.role_name);
                                                            // Show delete icon if the user is the uploader or has appropriate role
                                                            return (
                                                                <IconButton 
                                                                    size="small"
                                                                    onClick={() => handleFileDelete(file.id)}
                                                                    sx={{ 
                                                                        color: 'error.main',
                                                                        ml: 1,
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            );
                                                        })()}
                                                    </Box>
                                                ))}
                                        </Paper>
                                    )}

                                    {/* File Upload Area */}
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        hidden
                                        multiple
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.csv,.txt"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                    <Box
                                        component="div"
                                        onDragOver={handleDragOver}
                                        onDragEnter={handleDragEnter}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current.click()}
                                        sx={{
                                            mt: 2,
                                            border: '2px dashed #bdbdbd',
                                            borderRadius: '8px',
                                            p: 2,
                                            backgroundColor: dragActive ? '#f5f5f5' : '#fafafa',
                                            borderColor: dragActive ? '#2196f3' : '#bdbdbd',
                                            textAlign: 'center',
                                            cursor: isUploading ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative',
                                            opacity: isUploading ? 0.7 : 1,
                                            '&:hover': {
                                                borderColor: isUploading ? '#bdbdbd' : '#2196f3',
                                                backgroundColor: isUploading ? '#fafafa' : '#f5f5f5'
                                            }
                                        }}
                                    >
                                        {isUploading ? (
                                            <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, px: 3, pt: 3 }}>
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                    Uploading files... {uploadProgress}%
                                                </Typography>
                                                <mui.LinearProgress 
                                                    variant="determinate" 
                                                    value={uploadProgress}
                                                    sx={{
                                                        height: 8,
                                                        borderRadius: 4,
                                                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                        '& .MuiLinearProgress-bar': {
                                                            borderRadius: 4,
                                                            backgroundColor: '#2196f3'
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <>
                                                <CloudCircleIcon sx={{ fontSize: 36, color: '#bdbdbd', mb: 0.5 }} />
                                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                                    Drag & Drop Files Here
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    or click to select files
                                                </Typography>
                                                
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </StyledPaper>
                    </Grid>

                    {/* Comments Section */}
                    <Grid item xs={12}>
                        <StyledPaper sx={{ boxShadow: 'none' }}>
                            <SectionTitle variant="h6">
                                <DescriptionIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
                                Discussion 
                            </SectionTitle>
                            <List sx={{ mb: 2 }}>
                                {comments.map((comment, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem
                                            alignItems="flex-start"
                                            sx={{
                                                py: 2,
                                                px: 0
                                            }}
                                            onMouseEnter={() => {
                                                if (!comment.is_read && currentUser && comment.created_by !== `${currentUser.first_name} ${currentUser.last_name}`) {
                                                    auditService.markCommentAsRead(id, comment.id);
                                                    // Update the comment's read status locally
                                                    setComments(prevComments => 
                                                        prevComments.map(c => 
                                                            c.id === comment.id ? {...c, is_read: true} : c
                                                        )
                                                    );
                                                }
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    bgcolor: 'primary.main',
                                                    fontSize: '1rem',
                                                    mr: 2
                                                }}
                                            >
                                                {comment?.created_by?.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    position: 'relative'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '12px',
                                                        p: 2,
                                                        position: 'relative',
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: -8,
                                                            top: 12,
                                                            width: 0,
                                                            height: 0,
                                                            borderTop: '8px solid transparent',
                                                            borderBottom: '8px solid transparent',
                                                            borderRight: '8px solid #f5f5f5'
                                                        }
                                                    }}
                                                >
                                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: 'text.primary',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {comment?.created_by}
                                                            </Typography>
                                                            {!comment.is_read && currentUser && comment.created_by !== `${currentUser.first_name} ${currentUser.last_name}` && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: 'primary.main',
                                                                        display: 'inline-block'
                                                                    }}
                                                                />
                                                            )}
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                {new Date(comment.created_at).toLocaleString()}
                                                            </Typography>
                                                        </Box>
                                                        {comment.created_by === `${currentUser?.first_name} ${currentUser?.last_name}` && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => handleCommentMenuClick(e, comment)}
                                                                sx={{ 
                                                                    padding: 0.5,
                                                                    marginTop: -0.5,
                                                                    marginRight: -0.5
                                                                }}
                                                            >
                                                                <MoreVertIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                    {editingComment?.id === comment.id ? (
                                                        <Box sx={{ mt: 1 }}>
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={3}
                                                                value={editedCommentText}
                                                                onChange={(e) => setEditedCommentText(e.target.value)}
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                <Button
                                                                    size="small"
                                                                    onClick={handleCancelEdit}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => handleSaveEdit(comment.id)}
                                                                    disabled={!editedCommentText.trim()}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                color: 'rgba(0, 0, 0, 0.7)',
                                                                fontSize: '0.875rem',
                                                                lineHeight: 1.5,
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word'
                                                            }}
                                                        >
                                                            {comment?.comment_text}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </ListItem>
                                        {index < comments.length - 1 && (
                                            <Box sx={{ ml: 7, my: 1 }}>
                                                <Divider sx={{ opacity: 0.5 }} />
                                            </Box>
                                        )}
                                    </React.Fragment>
                                ))}
                            </List>
                            <Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    placeholder="Write a comment..."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    sx={{ 
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: '#f5f5f5',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0'
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#fff'
                                            }
                                        }
                                    }}
                                />
                                <Box display="flex" justifyContent="flex-end">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        endIcon={<SendIcon />}
                                        onClick={handleCommentSubmit}
                                        disabled={!comment.trim()}
                                        sx={{ 
                                            px: 3,
                                            py: 1,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: 'none',
                                                backgroundColor: 'primary.dark'
                                            }
                                        }}
                                    >
                                        Send Comment
                                    </Button>
                                </Box>
                            </Box>
                        </StyledPaper>
                    </Grid>
                </Grid>
            </Paper>

            <DynamicSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />

            <mui.Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, fileId: null })}
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        width: '320px',
                        overflow: 'hidden'
                    }
                }}
            >
                <mui.DialogContent sx={{ 
                    p: 2.5,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <DeleteIcon 
                        sx={{ 
                            fontSize: 40,
                            color: 'error.main',
                            opacity: 0.9,
                            mb: 2
                        }}
                    />
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 500,
                            textAlign: 'center',
                            color: 'text.primary',
                            mb: 0.5
                        }}
                    >
                        Delete File
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            textAlign: 'center',
                            mb: 2
                        }}
                    >
                        This action cannot be undone
                    </Typography>
                </mui.DialogContent>
                <mui.DialogActions 
                    sx={{ 
                        px: 2.5,
                        pb: 2.5,
                        pt: 0,
                        gap: 1,
                        justifyContent: 'center'
                    }}
                >
                    <Button 
                        onClick={() => setDeleteConfirm({ open: false, fileId: null })}
                        variant="outlined"
                        sx={{ 
                            minWidth: '96px',
                            borderColor: 'divider',
                            color: 'text.primary',
                            '&:hover': {
                                borderColor: 'text.primary',
                                backgroundColor: 'transparent'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                        sx={{ 
                            minWidth: '96px',
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Delete
                    </Button>
                </mui.DialogActions>
            </mui.Dialog>

            {/* Add Comment Menu */}
            <Menu
                anchorEl={commentMenuAnchorEl}
                open={Boolean(commentMenuAnchorEl)}
                onClose={handleCommentMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 1.5,
                        minWidth: 120
                    }
                }}
            >
                <MenuItem 
                    onClick={() => handleEditComment(selectedComment)}
                    sx={{ py: 1, px: 2 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem 
                    onClick={() => {
                        setDeleteCommentConfirm({ open: true, commentId: selectedComment.id });
                        handleCommentMenuClose();
                    }}
                    sx={{ py: 1, px: 2, color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Comment Confirmation Dialog */}
            <mui.Dialog
                open={deleteCommentConfirm.open}
                onClose={() => setDeleteCommentConfirm({ open: false, commentId: null })}
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        width: '320px'
                    }
                }}
            >
                <mui.DialogContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <DeleteIcon 
                        sx={{ 
                            fontSize: 40,
                            color: 'error.main',
                            opacity: 0.9,
                            mb: 2
                        }}
                    />
                    <Typography variant="h6" sx={{ mb: 1 }}>Delete Comment</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this comment? This action cannot be undone.
                    </Typography>
                </mui.DialogContent>
                <mui.DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button 
                        onClick={() => setDeleteCommentConfirm({ open: false, commentId: null })}
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained"
                        color="error"
                        onClick={handleDeleteComment}
                    >
                        Delete
                    </Button>
                </mui.DialogActions>
            </mui.Dialog>
            <CustomSpeedDial
                actions={[
                    {
                        icon: <AddIcon />,
                        name: 'Create New Request',
                        onClick: handleSpeedDialClick
                    }
                ]}
            />
        </ResponsiveContainer>
    );

    return (
        <React.Suspense fallback="Loading...">
            <div>
                <IASideBar mainContent={customMainContent} />
            </div>
        </React.Suspense>
    );
};

export default AuditRequestDetails; 